import { YCBConfigArray } from 'Config/types';

import type { BackgroundImageShape } from 'Containers/LoginPage/common/Background/types';

import { getBackgroundByVariant } from 'Assets/images/login/swipe-grid-background';

const MOBILE_KEY = 'mobile';
const DESKTOP_KEY = 'desktop';

export type LoginConfig = {
  loginPage: {
    backgroundImages: {
      mobile: BackgroundImageShape;
      desktop: BackgroundImageShape;
    };
    billboard: {
      img: null | {
        src: string;
        aspectRatio: string;
      };
      i18nKey: string;
    };
    disclaimer: boolean;
    footer: {
      blurbs: string[];
    };
  };
  loginPageExtraInfo: null | {
    color: string;
    i13nMessage: string;
    introText: string;
    link: string;
    title: string;
  };
};

const login: YCBConfigArray<LoginConfig> = [
  {
    settings: ['main'] as const,
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('en', MOBILE_KEY),
        desktop: getBackgroundByVariant('en', DESKTOP_KEY),
      },
      billboard: {
        img: null,
        i18nKey: 'LoginPageBillboard.swipeRightAlt',
      },
      disclaimer: true,
      footer: {
        blurbs: ['LoginPage.FooterBlurb1', 'LoginPage.FooterBlurb2'],
      },
    },
    loginPageExtraInfo: null,
  },
  {
    settings: ['country:US,CA'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('en-US', MOBILE_KEY),
        desktop: getBackgroundByVariant('en-US', DESKTOP_KEY),
      },
      // PYOT challenge winners https://gotinder.atlassian.net/browse/CREAT-84
      disclaimer: false,
    },
    loginPageExtraInfo: null,
  },
  // Asia
  {
    settings: ['country:CN'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('zh-Hans', MOBILE_KEY),
        desktop: getBackgroundByVariant('zh-Hans', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:KR'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('ko', MOBILE_KEY),
        desktop: getBackgroundByVariant('ko', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:JP'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('ja', MOBILE_KEY),
        desktop: getBackgroundByVariant('ja', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:TW'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('zh-Hant', MOBILE_KEY),
        desktop: getBackgroundByVariant('zh-Hant', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:South Asia'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('en-IN', MOBILE_KEY),
        desktop: getBackgroundByVariant('en-IN', DESKTOP_KEY),
      },
      billboard: {
        i18nKey: 'LoginPageBillboard.startSomethingEpic',
      },
    },
  },
  // SouthEast Asia
  {
    settings: ['country:TH'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('th', MOBILE_KEY),
        desktop: getBackgroundByVariant('th', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:ID'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('id', MOBILE_KEY),
        desktop: getBackgroundByVariant('id', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:VN'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('vi', MOBILE_KEY),
        desktop: getBackgroundByVariant('vi', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:Southeast Asia'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('sea', MOBILE_KEY),
        desktop: getBackgroundByVariant('sea', DESKTOP_KEY),
      },
    },
  },
  // Middle East
  {
    settings: ['country:Middle East'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('middle-east', MOBILE_KEY),
        desktop: getBackgroundByVariant('middle-east', DESKTOP_KEY),
      },
    },
  },
  // Arabic speaking country
  {
    settings: ['country:AE,BH,EG,IQ,JO,KW,LB,OM,PS,QA,SA,YE'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('ar', MOBILE_KEY),
        desktop: getBackgroundByVariant('ar', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:IL'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('he', MOBILE_KEY),
        desktop: getBackgroundByVariant('he', DESKTOP_KEY),
      },
    },
  },
  // South/Central America
  {
    settings: ['country:South America,Central America,MX'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('es', MOBILE_KEY),
        desktop: getBackgroundByVariant('es', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:BR'],
    // https://gotinder.atlassian.net/browse/TDP-165863
    loginPageExtraInfo: {
      color: 'rgba(246, 246, 246, 0.5)',
      i13nMessage: 'Clique aqui',
      introText: 'Baixe o Tinder',
      link: 'https://shared-rec.onelink.me/zPEy/wa76zynt',
      title: 'Clique aqui',
    },
  },
  // Europe
  {
    settings: ['country:FR'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('fr', MOBILE_KEY),
        desktop: getBackgroundByVariant('fr', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:DE'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('de', MOBILE_KEY),
        desktop: getBackgroundByVariant('de', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:ES'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('es', MOBILE_KEY),
        desktop: getBackgroundByVariant('es', DESKTOP_KEY),
      },
    },
  },
  {
    settings: ['country:GB,IE'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('en-GB', MOBILE_KEY),
        desktop: getBackgroundByVariant('en-GB', DESKTOP_KEY),
      },
    },
  },
  // Oceania
  {
    settings: ['country:AU,NZ'],
    loginPage: {
      backgroundImages: {
        mobile: getBackgroundByVariant('en-GB', MOBILE_KEY),
        desktop: getBackgroundByVariant('en-GB', DESKTOP_KEY),
      },
    },
  },
];

export default login;
